import { GLYPH_OWN_SIZE } from 'components/buttons/buttonStyle';
import { ModeEditOutlined as ModeEditOutlinedIcon } from 'components/icons';
import { FormattedDate } from 'components/misc/FormattedDate';
import { useModal } from 'components/modals/useModal';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { useId, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useUpdateInvoiceMatterCloseOnSendMutation } from 'store/api/invoice-details';
import { CloseOnSendDto, ConfidentialityDecision, UpdateCloseOnSendDto } from 'types/caseClosing';
import { FIELD_LABEL_CLASS } from 'components/GridItemList';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { CloseOnSendConfidentialityChoice, CloseOnSendConfidentialityModal } from './CloseOnSendConfidentialityModal';
import { DIALOG_560 } from 'components/modals/dialogWidths';

interface Props {
    invoiceId: number;
    matterId: number;
    isConfidential: boolean;
    closeOnSend: CloseOnSendDto | null;
    /**
     * The new design's read view: the choice as a field's value, ticked in place where the user may change it, the
     * lines under it, the decision's pencil.
     */
    isReadCell?: boolean;
    /**
     * The new design's case card in edit: the tick alone, its changes held by the card, whose Save sends the last one.
     * `pending` is that change, or null while there is none.
     */
    defer?: { pending: UpdateCloseOnSendDto | null; onChange: (body: UpdateCloseOnSendDto) => void };
}

const UNTICKED: UpdateCloseOnSendDto = { isRequested: false, confidentialityDecision: null, confidentialReason: null };

export function CloseOnSendTick({ invoiceId, matterId, isConfidential, closeOnSend, isReadCell, defer }: Props) {
    const checkboxId = useId();
    const labelId = useId();
    const { openModal, closeModal } = useModal();
    const [updateCloseOnSend, { isLoading: isSaving, originalArgs: savingArgs }] =
        useUpdateInvoiceMatterCloseOnSendMutation();
    const [isAwaitingDecision, setIsAwaitingDecision] = useState(false);
    // The PUT answers with the new state before the invoice it invalidates is refetched; the answer is shown
    // until the refetched invoice brings a different close-on-send object.
    const [saved, setSaved] = useState<{ basis: CloseOnSendDto | null; result: CloseOnSendDto } | null>(null);

    if (!closeOnSend) {
        return null;
    }

    const current = saved && saved.basis === closeOnSend ? saved.result : closeOnSend;
    const isTicked =
        isAwaitingDecision ||
        (defer?.pending
            ? defer.pending.isRequested
            : isSaving && savingArgs
              ? savingArgs.isRequested
              : current.isRequested);
    const isDisabled = !current.isEditable || isSaving || isAwaitingDecision;

    async function save(body: UpdateCloseOnSendDto) {
        if (defer) {
            defer.onChange(body);
            return;
        }
        const basis = closeOnSend;
        try {
            const result = await updateCloseOnSend({ invoiceId, matterId, ...body }).unwrap();
            setSaved({ basis, result });
        } catch (error) {
            console.error(error);
        }
    }

    function openConfidentialityModal(onCancel: () => void) {
        function cancel() {
            closeModal();
            onCancel();
        }
        function saveChoice({ decision, reason }: CloseOnSendConfidentialityChoice) {
            closeModal();
            setIsAwaitingDecision(false);
            void save({ isRequested: true, confidentialityDecision: decision, confidentialReason: reason });
        }
        openModal(
            <CloseOnSendConfidentialityModal closeOnSend={current} onCancel={cancel} onSave={saveChoice} />,
            cancel,
            undefined,
            undefined,
            DIALOG_560
        );
    }

    function handleTickChange(checked: boolean) {
        if (!checked) {
            void save(UNTICKED);
            return;
        }
        if (isConfidential) {
            setIsAwaitingDecision(true);
            openConfidentialityModal(() => setIsAwaitingDecision(false));
            return;
        }
        void save({ isRequested: true, confidentialityDecision: null, confidentialReason: null });
    }

    const confidentialityLineId =
        current.confidentialityDecision === ConfidentialityDecision.KEEP
            ? 'case-closing-invoice.confidentiality-keep'
            : 'case-closing-invoice.confidentiality-switch-off';
    // The decision is a record of who chose what and when, so it stays on the invoice once the case is no longer
    // confidential, as after confidentiality is switched off at closing.
    const showsDecision = current.isRequested && !!current.confidentialityDecision;
    const showsEditControl = isConfidential && current.isRequested && current.isEditable;

    const tick = (
        <div className="flex min-h-8 flex-row items-center gap-2">
            <Checkbox
                id={checkboxId}
                variant="secondary"
                checked={isTicked}
                disabled={isDisabled}
                onCheckedChange={handleTickChange}
            />
            <label
                htmlFor={checkboxId}
                className={cn('text-fg-secondary text-sm', isDisabled && 'cursor-not-allowed opacity-40')}
            >
                {t('case-closing-invoice.close-on-send')}
            </label>
        </div>
    );
    if (defer) {
        return tick;
    }

    const unsettledWarning =
        current.requiresNoUnsettledInvoices && current.isEditable
            ? t('case-closing-invoice.unsettled-invoices-warning')
            : null;
    const setByLine = current.isRequested ? (
        <FormattedMessage
            id="case-closing-invoice.set-by"
            values={{
                name: current.requestedBy?.name ?? '-',
                date: <FormattedDate key="date" value={current.requestedAt} />,
            }}
        />
    ) : null;
    const decisionLine = showsDecision ? (
        <FormattedMessage
            id={confidentialityLineId}
            values={{
                name: current.confidentialityDecidedBy?.name ?? '-',
                date: <FormattedDate key="date" value={current.confidentialityDecidedAt} />,
            }}
        />
    ) : null;
    const editControl = showsEditControl ? (
        <Button
            variant="ghost"
            size="icon-sm"
            aria-label={t('edit')}
            disabled={isSaving}
            className={GLYPH_OWN_SIZE}
            onClick={() => openConfidentialityModal(() => undefined)}
        >
            <ModeEditOutlinedIcon className="text-grey-500 new:text-primary" fontSize="small" />
        </Button>
    ) : null;

    if (isReadCell) {
        const lineClass = 'text-caption text-foreground-tertiary';
        const value = t(isTicked ? 'yes' : 'no');
        return (
            <div className={cn('flex min-w-0 flex-col gap-1', current.isRequested && 'col-span-full')}>
                <dt id={labelId} className={FIELD_LABEL_CLASS}>
                    {t('case-closing-invoice.close-on-send')}
                </dt>
                <dd className="m-0 flex flex-col gap-1 py-1">
                    {current.isEditable ? (
                        <span className="flex min-h-6 items-center gap-2">
                            <Checkbox
                                id={checkboxId}
                                aria-labelledby={labelId}
                                variant="secondary"
                                checked={isTicked}
                                disabled={isDisabled}
                                onCheckedChange={handleTickChange}
                            />
                            <label htmlFor={checkboxId} className="text-body1 font-medium">
                                {value}
                            </label>
                        </span>
                    ) : (
                        <span className="text-body1 font-medium">{value}</span>
                    )}
                    {setByLine ? <span className={lineClass}>{setByLine}</span> : null}
                    {decisionLine || editControl ? (
                        <span className={cn(lineClass, 'flex flex-wrap items-center gap-1')}>
                            {decisionLine}
                            {editControl}
                        </span>
                    ) : null}
                    {/* To whoever may tick the case, ticked or not: it says when a ticked case closes. */}
                    {unsettledWarning ? (
                        <span className={cn(lineClass, 'max-w-[72ch]')}>{unsettledWarning}</span>
                    ) : null}
                </dd>
            </div>
        );
    }

    return (
        <div className="flex flex-col items-end gap-0.5 text-right">
            {tick}
            {unsettledWarning ? <p className="text-fg-secondary max-w-prose text-xs">{unsettledWarning}</p> : null}
            {setByLine ? <p className="text-fg-secondary text-xs">{setByLine}</p> : null}
            {decisionLine || editControl ? (
                <div className="flex flex-row items-center gap-1">
                    {decisionLine ? <p className="text-fg-secondary text-xs">{decisionLine}</p> : null}
                    {editControl}
                </div>
            ) : null}
        </div>
    );
}
